import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { FileText, List, Hash, Globe, Link } from "lucide-react";

interface SchemaDisplayProps {
  schema?: any;
  data?: Record<string, unknown>;
  title?: string;
  className?: string;
  /**
   * Keys already rendered elsewhere on the page. Without this the job page
   * printed the full requirements text twice — once in its own section and
   * again here, because this component renders every inputData key generically.
   */
  omitKeys?: string[];
}

// Humanize a camelCase / snake_case key into a readable label.
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

// Mirror of DataVisualizer.renderValue, with an explicit "Not provided" chip for
// empty values instead of letting the row vanish.
function renderValue(value: unknown) {
  if (isEmptyValue(value)) {
    return (
      <Badge variant="outline" className="text-[10px] text-muted-foreground border-divider">
        Not provided
      </Badge>
    );
  }

  // Arrays (Lists)
  if (Array.isArray(value)) {
    return (
      <div className="space-y-1 mt-1">
        {value.map((item, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
            <span>{typeof item === "object" ? JSON.stringify(item) : String(item)}</span>
          </div>
        ))}
      </div>
    );
  }

  // Objects (Nested)
  if (typeof value === "object") {
    return (
      <div className="pl-3 border-l-2 border-divider mt-2 space-y-2">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k}>
            <span className="text-xs font-mono text-muted-foreground uppercase">{k}: </span>
            <span className="text-sm">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
    );
  }

  // Booleans
  if (typeof value === "boolean") {
    return <Badge variant={value ? "default" : "secondary"}>{value ? "Yes" : "No"}</Badge>;
  }

  // URLs
  if (typeof value === "string" && (value.startsWith("http") || value.startsWith("www"))) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all flex items-center gap-1"
      >
        <Link className="h-3 w-3" /> {value}
      </a>
    );
  }

  // Long Text (Description)
  if (typeof value === "string" && value.length > 100) {
    return <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{value}</p>;
  }

  return <span className="text-sm font-medium">{String(value)}</span>;
}

function getIcon(key: string) {
  const k = key.toLowerCase();
  if (k.includes("list") || k.includes("array") || k.includes("keywords")) return List;
  if (k.includes("count") || k.includes("number") || k.includes("amount")) return Hash;
  if (k.includes("url") || k.includes("site") || k.includes("web")) return Globe;
  return FileText;
}

interface FieldRowProps {
  label: string;
  description?: string;
  value: unknown;
  icon: ReturnType<typeof getIcon>;
}

function FieldRow({ label, description, value, icon: Icon }: FieldRowProps) {
  const isLongText = typeof value === "string" && value.length > 100;
  return (
    <div>
      <div className={`flex ${isLongText ? "flex-col gap-2" : "flex-row items-start justify-between gap-4"}`}>
        <div className="flex flex-col gap-0.5 min-w-[120px]">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon className="h-4 w-4 opacity-50" />
            <span>{label}</span>
          </div>
          {description && <span className="text-[11px] text-muted-foreground/70 pl-6">{description}</span>}
        </div>
        <div className={`${isLongText ? "w-full bg-surface-subtle p-3 rounded-md" : "text-right flex-1"}`}>
          {renderValue(value)}
        </div>
      </div>
    </div>
  );
}

/**
 * Read-only counterpart to SchemaForm. Renders a template's JSON-schema fields
 * against a data object: each property shows its title/label, its description as
 * helper text, and the resolved value (with a "Not provided" chip for empties).
 * Keys present in `data` but absent from the schema fall into a trailing
 * "Additional parameters" group, so nothing silently disappears.
 */
export function SchemaDisplay({
  schema,
  data,
  title,
  className,
  omitKeys = [],
}: SchemaDisplayProps) {
  const omit = new Set(omitKeys);
  const allProperties: Record<string, any> = schema?.properties ?? {};
  const properties: Record<string, any> = Object.fromEntries(
    Object.entries(allProperties).filter(([k]) => !omit.has(k)),
  );
  const schemaKeys = Object.keys(properties);
  const dataObj: Record<string, unknown> = data ?? {};
  const extraKeys = Object.keys(dataObj).filter(
    (k) => !(k in allProperties) && !omit.has(k),
  );

  const hasNothing = schemaKeys.length === 0 && extraKeys.length === 0;

  if (hasNothing) {
    return (
      <Card className={`glass-panel ${className ?? ""}`}>
        {title && (
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">{title}</CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="p-4 text-center text-muted-foreground text-sm italic border border-dashed border-divider rounded-lg">
            No specifications provided.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`glass-panel ${className ?? ""}`}>
      {title && (
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4">
        {schemaKeys.map((key, index) => {
          const prop = properties[key] ?? {};
          return (
            <div key={key}>
              <FieldRow
                label={prop.title || humanizeKey(key)}
                description={prop.description}
                value={dataObj[key]}
                icon={getIcon(key)}
              />
              {index < schemaKeys.length - 1 && <Separator className="my-3 bg-surface-subtle" />}
            </div>
          );
        })}

        {extraKeys.length > 0 && (
          <div className="space-y-4">
            {schemaKeys.length > 0 && <Separator className="my-3 bg-surface-subtle" />}
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Additional parameters
            </p>
            {extraKeys.map((key, index) => (
              <div key={key}>
                <FieldRow label={humanizeKey(key)} value={dataObj[key]} icon={getIcon(key)} />
                {index < extraKeys.length - 1 && <Separator className="my-3 bg-surface-subtle" />}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
